import React from 'react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import {
    formatDollarsEarningsFilter,
    locale,
    updateAndReplaceURLSearchParams,
} from '@trqk/utils';

import { Tooltip, IconEdit } from '../../..';
import { defaultFilterWidth, defaultFilterHeight } from '../../constants';
import { EarningsPreFilterContent } from './EarningsPreFilterContent';

interface EarningsPreFilterProps<TData extends Record<string, unknown>> {
    /**
     * First element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    current: PreFilterState<TData>;
    /**
     * Height for button that opens the filter
     */
    height?: number;
    /**
     * The ID for the max value filter
     */
    maxFilterId: string;
    /**
     * The ID for the min value filter
     */
    minFilterId: string;
    /**
     * Second element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    send: SendPreFilterEvent<TData>;
    /**
     * Width for button that opens the filter
     */
    width?: number;
}

/**
 * Opens a tooltip to allow for filtering by a range of earnings.
 * @param {PerformancesTableFilterProps} props
 */
export function EarningsPreFilter<TData extends Record<string, unknown>>({
    current,
    height = defaultFilterHeight,
    maxFilterId,
    minFilterId,
    send,
    width = defaultFilterWidth,
}: EarningsPreFilterProps<TData>): JSX.Element {
    const history = useHistory();
    const maxFilterConfig = current.context.preFilterConfigs.get(maxFilterId);
    const minFilterConfig = current.context.preFilterConfigs.get(minFilterId);

    if (maxFilterConfig === undefined) {
        throw new Error(
            'Could not find maxFilterConfig within preFilterConfigs. Did you add a matching config to the `preFilterConfigs` array passed to `getPreFilterMachine`?',
        );
    }

    if (minFilterConfig === undefined) {
        throw new Error(
            'Could not find minFilterConfig within preFilterConfigs. Did you add a matching config to the `preFilterConfigs` array passed to `getPreFilterMachine`?',
        );
    }

    const maxFilterValue =
        current.context.preFilterValues.get(maxFilterId)?.value ?? null;
    const max = _get(maxFilterValue, maxFilterConfig.fieldValuePath) ?? null;

    const minFilterValue =
        current.context.preFilterValues.get(minFilterId)?.value ?? null;
    const min = _get(minFilterValue, minFilterConfig.fieldValuePath) ?? null;

    const label =
        min === null || max === null
            ? locale.enUS.STATEMENTS.PERFORMANCES.FILTERS.EARNINGS.ALL
            : `${formatDollarsEarningsFilter(
                  min,
                  -Number.MAX_SAFE_INTEGER,
              )} to ${formatDollarsEarningsFilter(
                  max,
                  Number.MAX_SAFE_INTEGER,
              )}`;

    const filterButton = (
        <div
            className={cx('flex items-center justify-between rounded border', {
                'border-tertiary-200': minFilterValue === null,
                'border-primary-500': minFilterValue !== null,
            })}
            style={{ width, height }}
        >
            <div className="ml-2 font-semibold">{label}</div>
            <IconEdit fillColor="tertiary-200" width={16} addClasses="mr-3" />
        </div>
    );

    const deleteFilter = () => {
        // Send the event that updates the preFilterValues
        send({
            type: 'SET_PRE_FILTER_VALUES',
            value: {
                preFilterValues: [
                    {
                        id: minFilterId,
                        value: null,
                    },
                    {
                        id: maxFilterId,
                        value: null,
                    },
                ],
            },
        });

        // Delete the corresponding URL parameters
        updateAndReplaceURLSearchParams(history, [
            {
                key: minFilterConfig.urlParamKey,
                value: null,
            },
            {
                key: maxFilterConfig.urlParamKey,
                value: null,
            },
        ]);
    };

    const setFilter = (filterValue: EarningsPreFilterValue): void => {
        const nextMinFilterValue = {
            id: minFilterId,
            value: null,
        };

        _set(
            nextMinFilterValue,
            minFilterConfig.filterValuePath,
            filterValue[0],
        );

        const nextMaxFilterValue = {
            id: maxFilterId,
            value: null,
        };

        _set(
            nextMaxFilterValue,
            maxFilterConfig.filterValuePath,
            filterValue[1],
        );

        // Send the event that updates the preFilterValues
        send({
            type: 'SET_PRE_FILTER_VALUES',
            value: {
                preFilterValues: [nextMinFilterValue, nextMaxFilterValue],
            },
        });

        // Set the corresponding URL parameters
        updateAndReplaceURLSearchParams(history, [
            {
                key: minFilterConfig.urlParamKey,
                value: filterValue[0],
            },
            {
                key: maxFilterConfig.urlParamKey,
                value: filterValue[1],
            },
        ]);
    };

    return (
        <Tooltip
            RenderMessage={({
                close,
            }: RenderTooltipMessageProps): JSX.Element => (
                <EarningsPreFilterContent
                    deleteFilter={deleteFilter}
                    filterValue={[min, max]}
                    setFilter={setFilter}
                    closeTooltip={close}
                />
            )}
            padding="none"
            position="bottom"
            background="light"
            maxWidth={500}
            minWidth={500}
        >
            {filterButton}
        </Tooltip>
    );
}
